import { ActionTypes } from "@shared/interfaces";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as sharedActions, selectors as sharedSelectors } from "@shared/store";

import useDebounce from "./DebounceHook";

interface UseLoaderProps {
  name: string;
  actionTypes: ActionTypes[];
}

export const useLoader = ({ name, actionTypes }: UseLoaderProps) => {
  const dispatch = useDispatch();
  const loadingSection = useSelector(sharedSelectors.getLoadingSection(name));

  const isLoading = useMemo(() => {
    return !!loadingSection && !!loadingSection.length;
  }, [loadingSection]);

  const isLoadingDebounced = useDebounce(isLoading, 750);

  // Several call sites pass a fresh array literal (`actionTypes: [X.getY]`) rather than memoizing
  // it, so depending the effect on `actionTypes` itself tears the loader down and re-registers it
  // on every render for those callers — see 923-core-context context/engineering/spark-rewards/open-follow-ups.md #65. Two
  // different string instances with the same characters are `===` in JS, so joining the request-type
  // strings gives a primitive the effect can depend on that stays stable across renders whenever the
  // underlying action creators haven't changed, regardless of whether the caller memoizes the array
  // itself.
  //
  // Accepted limitation, not a bug: this key is derived only from each action type's
  // `.request().type`, not `.success()`/`.failure()`. Two action sets that shared request types but
  // differed in success/failure types would theoretically not re-register correctly. Unreachable in
  // this codebase specifically: every action-type constant across the app is globally unique
  // (verified: swept all action-type constant files, 330 distinct string values, zero collisions), so
  // two different async actions can never share a request type to begin with.
  const actionTypesKey = actionTypes.map((actionType) => actionType.request().type).join("|");

  useEffect(() => {
    if (name && actionTypes) {
      const loadingType = actionTypes.reduce(
        (acc, actionType) => {
          const requestType = actionType.request().type;
          const successType = actionType.success().type;
          const errorType = actionType.failure().type;

          acc.startActions.push(requestType);

          acc.stopActions[successType] = requestType;
          acc.stopActions[errorType] = requestType;

          return acc;
        },
        {
          name,
          startActions: [],
          stopActions: {},
        } as {
          name: string;
          startActions: string[];
          stopActions: { [key: string]: string };
        },
      );

      dispatch(sharedActions.addLoadingType(loadingType));
    }

    return () => {
      dispatch(sharedActions.removeLoadingType(name));
    };
    // actionTypesKey (derived above from the same array) is the real dependency; actionTypes itself
    // is a fresh reference on every render and would defeat the point of the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, dispatch, actionTypesKey]);

  return { isLoading, isLoadingDebounced: isLoading || isLoadingDebounced, loaderName: name };
};

export default useLoader;
